import { deleteField, Timestamp } from "firebase/firestore";
import {
  AlertCircle,
  CalendarDays,
  Clock,
  FileUp,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import {
  instantToWallTime,
  resolveWallTime,
  usableTimeZone,
} from "../../event-experience/agenda-schedule";
import { AgendaSchedule } from "../../event-experience/AgendaSchedule";
import type { Track } from "../../event-experience/schemas";
import {
  highlightChapterWarning,
  highlightFields,
  highlightFormFrom,
  highlightProblems,
  type HighlightForm,
} from "../agenda-highlight";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import type { AdminAgendaItem as AgendaItem } from "../agenda-item-schema";
import { outsideEventDates } from "../event-dates-fit";
import type { EventSettings } from "../schemas";
import { useAdminCommand } from "../use-admin-command";
import {
  useAdminAgenda,
  useAdminSpeakers,
  useAdminTracks,
  useAdminVenues,
} from "../use-admin-content";
import { useAttendeeRecords } from "../use-attendee-records";
import { deleteEntityDoc, useEntityWrites } from "../use-entity-writes";
import {
  BulkDeleteDialog,
  BulkRowCheckbox,
  BulkSelectBar,
  BulkSelectButton,
} from "../bulk/BulkDelete";
import { useBulkDelete } from "../bulk/use-bulk-delete";
import { clockName, COMPLETE, describeWall, TimeField } from "../WallTimeField";

import { AgendaHighlightFields } from "./AgendaHighlightFields";
import { AgendaImportPanel } from "./AgendaImportPanel";
import { OutsideDatesNote } from "./OutsideDatesNote";
import { DayTitlesEditor } from "./DayTitlesEditor";

const AGENDA_NOUN = { singular: "agenda item", plural: "agenda items" };

/**
 * What an unresolved presenter id means, said as precisely as the data allows.
 *
 * Two causes land an id in `unresolvedPresenterIds`, and they are different
 * problems. An id matching no roster row and no speaker is a data error, and
 * only its count can be reported: nothing anywhere holds a name for it. An id
 * whose holder withdrew from the event is a person who left, and the roster
 * row they left behind still carries their name, so the card names them. The
 * organiser is the one who has to find a replacement, which is why the
 * distinction is worth the branch.
 *
 * Returns null when nothing is wrong, so the caller renders nothing.
 */
export function unresolvedPresenterMessage(
  unresolvedIds: string[] | undefined,
  withdrawnNames: Map<string, string>,
): string | null {
  if (!unresolvedIds || unresolvedIds.length === 0) return null;
  const withdrawn = unresolvedIds
    .map((id) => withdrawnNames.get(id))
    .filter((name): name is string => name !== undefined);
  const unknown = unresolvedIds.length - withdrawn.length;

  const clauses: string[] = [];
  if (withdrawn.length > 0) {
    clauses.push(`${joinNames(withdrawn)} withdrew from the event`);
  }
  if (unknown > 0) {
    clauses.push(
      unknown === 1
        ? "1 presenter is not an attendee or a speaker of this event"
        : `${unknown} presenters are not attendees or speakers of this event`,
    );
  }
  return `${clauses.join(", and ")}, so this session does not show them.`;
}

function joinNames(names: string[]): string {
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** One empty list, so a loading tab hands the memoised schedule a stable prop. */
const NO_ITEMS: never[] = [];

type PresenterOption = { id: string; label: string };
type AgendaWrites = ReturnType<typeof useEntityWrites>;
type AdminCommand = ReturnType<typeof useAdminCommand>;

/**
 * The agenda, where editing splits in two.
 *
 * Everything except the times is a direct write. `startsAt` and `endsAt` are
 * absent from the update key list, so moving a session is a RESCHEDULE_AGENDA
 * command instead — its trigger recomputes `dayKey` from the event timezone,
 * which is also why the client must never send that field itself.
 *
 * Times are entered and listed on the event's clock, `timezone`, whatever
 * clock the organiser's device is on. The trigger derives `dayKey` from the
 * same zone, so the day a session is listed under here is the day it is
 * stored under.
 *
 * The create, edit and reschedule forms each hold their own state in their
 * own component. Typing then re-renders the form alone, and the schedule
 * beside it, which can hold a hundred or more sessions, stays as it is.
 */
export function AgendaSection({
  eventId,
  uid,
  timezone,
  event,
  reloadEvent,
}: {
  eventId: string;
  uid: string;
  /** The event's IANA zone, from events/{id}. */
  timezone?: string;
  /**
   * The event's dates and day titles, for the day titles editor. Without them
   * the editor is not offered.
   */
  event?: Pick<EventSettings, "startDate" | "endDate" | "dayTitles">;
  /** Reloads the event after the day titles are saved. */
  reloadEvent?: () => void;
}) {
  const agenda = useAdminAgenda(eventId);
  const tracks = useAdminTracks(eventId);
  // D14: a presenter is an attendee OR a speaker.
  //
  // `presenterIds` is resolved by firestore_attendee_reference_repository,
  // which looks each id up in events/{id}/attendees first and then in
  // events/{id}/speakers. Attendee-first is what keeps every id written before
  // D14 resolving exactly as it did. An id matching neither is listed in
  // `unresolvedPresenterIds` and reported on the card, rather than being
  // deleted from the array with nothing said.
  const presenters = useAttendeeRecords(eventId);
  const speakers = useAdminSpeakers(eventId);

  // Names for the ids that stopped resolving because their holder withdrew.
  // The roster row survives a withdrawal, carrying the name, which is what
  // lets the card below say who left instead of printing a bare count.
  const withdrawnPresenterNames = useMemo(() => {
    const names = new Map<string, string>();
    if (presenters.status === "success") {
      for (const record of presenters.data) {
        if (record.status === "withdrawn") names.set(record.id, record.name);
      }
    }
    return names;
  }, [presenters]);
  const writes = useEntityWrites(eventId, "agenda_items");
  const command = useAdminCommand();

  // Attendees first, then the speakers who are not already one of them. A
  // speaker carrying an `attendeeId` is the same human as a roster row that is
  // offered above, so listing both would put one person on the chip list
  // twice under two ids that resolve to two different projections.
  //
  // The speaker chips are what D14 buys: an external presenter is a speaker
  // document with no `attendeeId`, and until now there was no way to attach
  // one to their own session.
  const presenterOptions = useMemo<PresenterOption[]>(
    () => [
      ...(presenters.status === "success" ? presenters.data : []).map((a) => ({
        id: a.id,
        label: a.name,
      })),
      ...(speakers.status === "success" ? speakers.data : [])
        .filter((speaker) => speaker.attendeeId === undefined)
        .map((speaker) => ({
          id: speaker.id,
          label: `${speaker.name} (speaker)`,
        })),
    ],
    [presenters, speakers],
  );
  const trackList = tracks.status === "success" ? tracks.data : NO_ITEMS;

  // `null` is closed, `"new"` is the create form, an item is its edit form.
  const [editing, setEditing] = useState<AgendaItem | "new" | null>(null);
  const [rescheduling, setRescheduling] = useState<AgendaItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgendaItem | null>(null);
  const [importing, setImporting] = useState(false);

  const reloadAgenda = agenda.reload;
  const bulk = useBulkDelete({
    deleteOne: (id) => deleteEntityDoc(eventId, "agenda_items", id),
    onDeleted: reloadAgenda,
  });
  const { selecting, selected, toggle } = bulk;
  useEffect(() => {
    if (command.state.status === "succeeded") {
      setRescheduling(null);
      reloadAgenda();
    }
  }, [command.state.status, reloadAgenda]);

  const items = agenda.status === "success" ? agenda.data : NO_ITEMS;
  const bulkItems = useMemo(
    () => items.map((item) => ({ id: item.id, label: item.title })),
    [items],
  );

  // Undefined when the event has no zone this browser can resolve. The list
  // and the fields then fall back to the device's clock together, and the
  // notice below says so.
  const zone = usableTimeZone(timezone);

  // Sessions on days the event's dates do not include, judged on the event's
  // clock like the schedule below.
  const outsideDates = useMemo(
    () =>
      event
        ? outsideEventDates(
            items.map((item) => item.startsAt),
            event,
            zone,
          )
        : null,
    [items, event, zone],
  );

  const renderNotice = useCallback(
    (item: AgendaItem) => {
      // D14: the drop stops being silent. Before this, an id that matched
      // nobody was deleted by the trigger and the only trace was a chip that
      // used to be there.
      const message = unresolvedPresenterMessage(
        item.unresolvedPresenterIds,
        withdrawnPresenterNames,
      );
      return (
        message && (
          <p
            className="mt-2 flex items-center gap-1.5 text-xs text-destructive-text"
            data-testid={`agenda-unresolved-presenters-${item.id}`}
          >
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {message}
          </p>
        )
      );
    },
    [withdrawnPresenterNames],
  );

  const renderActions = useCallback(
    (item: AgendaItem) =>
      selecting ? (
        <BulkRowCheckbox
          withText
          checked={selected.has(item.id)}
          onChange={() => toggle(item.id)}
          name={item.title}
          testId={`select-agenda-${item.id}`}
        />
      ) : (
        <>
          <Button
            variant="ghost"
            size="icon"
            className={ICON_BUTTON}
            aria-label={`Reschedule ${item.title}`}
            title="Change the start and end time"
            data-testid={`reschedule-agenda-${item.id}`}
            onClick={() => setRescheduling(item)}
          >
            <Clock className="h-4 w-4 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={ICON_BUTTON}
            aria-label={`Edit ${item.title}`}
            data-testid={`edit-agenda-${item.id}`}
            onClick={() => setEditing(item)}
          >
            <Pencil className="h-4 w-4 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={ICON_BUTTON}
            aria-label={`Delete ${item.title}`}
            data-testid={`delete-agenda-${item.id}`}
            onClick={() => setPendingDelete(item)}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </>
      ),
    [selecting, selected, toggle],
  );

  if (agenda.status === "loading") return <LoadingCards cards={3} />;
  if (agenda.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load the agenda">
        {agenda.error}
      </EmptyState>
    );

  return (
    <div className="space-y-4" data-testid="admin-agenda-content">
      <div className="flex items-center justify-between gap-4">
        <Badge variant="outline">
          {items.length} {items.length === 1 ? "item" : "items"}
        </Badge>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => setImporting(true)}
            disabled={importing}
            data-testid="import-agenda"
          >
            <FileUp className="h-4 w-4" />
            Import CSV
          </Button>
          {!selecting && items.length > 0 && (
            <BulkSelectButton
              onClick={bulk.enter}
              testId="select-agenda-items"
            />
          )}
          <Button
            size="sm"
            variant="cta"
            onClick={() => setEditing("new")}
            data-testid="add-agenda-item"
          >
            <Plus className="h-4 w-4" />
            Add item
          </Button>
        </div>
      </div>

      {event && reloadEvent && (
        <OutsideDatesNote
          eventId={eventId}
          uid={uid}
          timeZone={zone}
          event={event}
          reload={reloadEvent}
          info={outsideDates}
          subject="agenda"
          testId="agenda-outside-dates"
        />
      )}

      {event && reloadEvent && (
        <DayTitlesEditor eventId={eventId} event={event} reload={reloadEvent} />
      )}

      {importing && (
        <AgendaImport
          eventId={eventId}
          timeZone={zone}
          rawTimeZone={timezone}
          tracks={tracks.status === "success" ? tracks.data : null}
          speakers={speakers.status === "success" ? speakers.data : null}
          attendees={presenters.status === "success" ? presenters.data : null}
          existing={items}
          uid={uid}
          event={event}
          reloadEvent={reloadEvent}
          loadError={firstLoadError([tracks, speakers, presenters])}
          onClose={() => setImporting(false)}
          onImported={reloadAgenda}
        />
      )}

      {selecting && (
        <BulkSelectBar
          bulk={bulk}
          visible={bulkItems}
          noun={AGENDA_NOUN}
          testId="bulk-agenda"
        />
      )}

      <AdminCommandFeedback
        state={command.state}
        onReset={command.reset}
        testId="agenda-reschedule"
      />
      {writes.error && (
        <p className="text-sm text-destructive-text" data-testid="agenda-error">
          {writes.error}
        </p>
      )}

      {!zone && (
        <p
          className="flex items-start gap-2 rounded-md border border-status-warning bg-status-warning/15 px-3 py-2 text-sm text-foreground"
          data-testid="agenda-clock-warning"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {timezone
              ? `This event's time zone, ${timezone}, is not one this browser knows.`
              : "This event has no time zone."}{" "}
            The list and the time fields use your device&apos;s clock until one
            is chosen on the Settings tab.
          </span>
        </p>
      )}

      {items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No agenda items">
          Add the sessions that make up this event.
        </EmptyState>
      ) : (
        <AgendaSchedule
          items={items}
          tracks={trackList}
          timeZone={zone}
          idPrefix="admin-agenda"
          stackActionsOnPhone
          renderNotice={renderNotice}
          renderActions={renderActions}
        />
      )}

      {editing !== null && (
        <AgendaItemDialog
          key={editing === "new" ? "new" : editing.id}
          editing={editing === "new" ? null : editing}
          timeZone={zone}
          tracks={trackList}
          items={items}
          presenterOptions={presenterOptions}
          writes={writes}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            agenda.reload();
          }}
        />
      )}

      {rescheduling !== null && (
        <RescheduleDialog
          key={rescheduling.id}
          item={rescheduling}
          timeZone={zone}
          eventId={eventId}
          uid={uid}
          command={command}
          onClose={() => setRescheduling(null)}
        />
      )}

      <BulkDeleteDialog
        bulk={bulk}
        noun={AGENDA_NOUN}
        testId="bulk-delete-agenda"
      />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete agenda item?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pendingDelete?.title} will be removed from the agenda.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={writes.saving}
              data-testid="confirm-delete-agenda"
              onClick={async () => {
                if (!pendingDelete) return;
                const ok = await writes.remove(pendingDelete.id);
                setPendingDelete(null);
                if (ok) agenda.reload();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The import panel with the venues it matches against, read only while it is
 * open: the agenda tab has no other use for them.
 */
function AgendaImport({
  loadError,
  ...props
}: Omit<ComponentProps<typeof AgendaImportPanel>, "venues">) {
  const venues = useAdminVenues(props.eventId);
  return (
    <AgendaImportPanel
      {...props}
      venues={venues.status === "success" ? venues.data : null}
      loadError={loadError ?? (venues.status === "error" ? venues.error : null)}
    />
  );
}

function firstLoadError(
  sources: ({ status: "error"; error: string } | { status: string })[],
): string | null {
  for (const source of sources) {
    if ("error" in source) return source.error;
  }
  return null;
}

/** 44px square on a phone, the smallest target a thumb hits reliably. */
const ICON_BUTTON = "h-11 w-11 sm:h-10 sm:w-10";

/**
 * The create and edit form, holding its own state.
 *
 * Mounted only while open, under a key per item, so each opening starts from
 * the item's stored values or from blank without a reset step.
 */
function AgendaItemDialog({
  editing,
  timeZone,
  tracks,
  items,
  presenterOptions,
  writes,
  onClose,
  onSaved,
}: {
  editing: AgendaItem | null;
  timeZone: string | undefined;
  tracks: Track[];
  /** Every session, to warn when a fourth would be highlighted. */
  items: AgendaItem[];
  presenterOptions: PresenterOption[];
  writes: AgendaWrites;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    title: editing?.title ?? "",
    description: editing?.description ?? "",
    roomName: editing?.roomName ?? "",
    location: editing?.location ?? "",
    locationNotes: editing?.locationNotes ?? "",
  });
  // Separate from `form` because these are arrays, not the string fields the
  // rest of the form holds.
  const [trackIds, setTrackIds] = useState<string[]>(editing?.trackIds ?? []);
  const [presenterIds, setPresenterIds] = useState<string[]>(
    editing?.presenterIds ?? [],
  );
  const [times, setTimes] = useState({ startsAt: "", endsAt: "" });
  const [highlight, setHighlight] = useState<HighlightForm>(() =>
    highlightFormFrom(editing),
  );
  const [problem, setProblem] = useState<string | null>(null);
  // Shown once the organiser has tried to save, so a chapter text is not
  // called too long while it is still being typed.
  const [tried, setTried] = useState(false);
  const highlightIssues = highlightProblems(highlight);
  const chapterWarning = highlightChapterWarning(
    items,
    editing?.id ?? null,
    highlight.on,
  );

  async function save() {
    setTried(true);
    if (!form.title.trim()) {
      setProblem("Title is required.");
      return;
    }
    const optionalProblem = firstOptionalProblem(form);
    if (optionalProblem) {
      setProblem(optionalProblem);
      return;
    }
    if (Object.keys(highlightIssues).length > 0) {
      const which = highlightIssues.title
        ? highlightIssues.blurb
          ? "title and text"
          : "title"
        : "text";
      setProblem(`Shorten the chapter ${which}, marked above.`);
      return;
    }
    if (editing) {
      // Times are deliberately absent: they are command-owned.
      const ok = await writes.update(editing.id, {
        title: form.title.trim(),
        // Blank REMOVES rather than writing "". isOptionalBoundedString would
        // accept "", but a stored empty string is a value the attendee side
        // then has to defend against on every read.
        //
        // description and roomName join the rule. Both are z.string().optional()
        // and every reader already gates on truthiness, so "" and absent behave
        // identically today; writing "" was the odd one out three lines up.
        description: optionalText(form.description),
        roomName: optionalText(form.roomName),
        location: optionalText(form.location),
        locationNotes: optionalText(form.locationNotes),
        trackIds,
        presenterIds,
        ...highlightFields(highlight, "update"),
      });
      if (ok) onSaved();
      return;
    }

    const parsed = parseTimes(
      times,
      timeZone,
      "Start and end times are required.",
    );
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    const { start, end } = parsed;
    const created = await writes.create({
      title: form.title.trim(),
      ...(form.description.trim()
        ? { description: form.description.trim() }
        : {}),
      ...(form.roomName.trim() ? { roomName: form.roomName.trim() } : {}),
      ...(form.location.trim() ? { location: form.location.trim() } : {}),
      ...(form.locationNotes.trim()
        ? { locationNotes: form.locationNotes.trim() }
        : {}),
      startsAt: Timestamp.fromDate(start),
      endsAt: Timestamp.fromDate(end),
      trackIds,
      presenterIds,
      ...highlightFields(highlight, "create"),
    });
    if (created) onSaved();
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit agenda item" : "Add agenda item"}
          </DialogTitle>
        </DialogHeader>
        {/* Scrolls, like the shared CRUD dialog's body. Without it the
            footer leaves the viewport once the form is this tall, and Save
            is unreachable. */}
        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="agenda-title">Title *</Label>
            <Input
              id="agenda-title"
              testId="agenda-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agenda-description">Description</Label>
            <Textarea
              id="agenda-description"
              data-testid="agenda-description"
              rows={3}
              value={form.description}
              placeholder="What this session is about."
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
            <p className="text-xs text-muted-foreground">
              Shown when a participant expands the session.
            </p>
          </div>
          {!editing && (
            <div className="space-y-2">
              <div className="grid gap-4 sm:grid-cols-2">
                <TimeField
                  label="Starts"
                  testId="agenda-starts"
                  value={times.startsAt}
                  onChange={(v) => setTimes({ ...times, startsAt: v })}
                />
                <TimeField
                  label="Ends"
                  testId="agenda-ends"
                  value={times.endsAt}
                  onChange={(v) => setTimes({ ...times, endsAt: v })}
                />
              </div>
              <ClockNote
                timeZone={timeZone}
                times={times}
                testId="agenda-times-clock"
              />
            </div>
          )}
          {editing && (
            <p className="text-xs text-muted-foreground">
              Times are changed with the reschedule control, so the day they
              fall on is recalculated in the event&apos;s timezone.
            </p>
          )}
          <MultiPicker
            label="Tracks"
            testId="agenda-tracks"
            max={50}
            options={tracks.map((t) => ({ id: t.id, label: t.name }))}
            selected={trackIds}
            onToggle={setTrackIds}
            empty="No tracks yet. Add them on the Tracks tab."
          />

          <MultiPicker
            label="Presenters"
            testId="agenda-presenters"
            max={20}
            options={presenterOptions}
            selected={presenterIds}
            onToggle={setPresenterIds}
            empty="No attendees or speakers yet. Add somebody on the Attendees or Speakers tab."
          />

          <div className="space-y-1.5">
            <Label htmlFor="agenda-room">Room</Label>
            <Input
              id="agenda-room"
              testId="agenda-room"
              value={form.roomName}
              onChange={(e) => setForm({ ...form, roomName: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="agenda-location">Location</Label>
            <Input
              id="agenda-location"
              testId="agenda-location"
              value={form.location}
              placeholder="Atrium, east wing"
              onChange={(e) => setForm({ ...form, location: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Shown beside the room, for a place that is neither.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="agenda-location-notes">Location notes</Label>
            <Textarea
              id="agenda-location-notes"
              data-testid="agenda-location-notes"
              rows={3}
              value={form.locationNotes}
              placeholder="Take the lift to the fourth floor and turn left."
              onChange={(e) =>
                setForm({ ...form, locationNotes: e.target.value })
              }
            />
            <p className="text-xs text-muted-foreground">
              How to find the room once somebody is at the address. Shown under
              it on the session.
            </p>
          </div>

          <AgendaHighlightFields
            value={highlight}
            onChange={setHighlight}
            problems={tried ? highlightIssues : {}}
            warning={chapterWarning}
            stored={{
              title: editing?.highlightTitle?.trim() ?? "",
              blurb: editing?.highlightBlurb?.trim() ?? "",
            }}
            disabled={writes.saving}
          />

          {problem && (
            <p
              className="text-sm text-destructive-text"
              data-testid="agenda-form-error"
            >
              {problem}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="cta"
            onClick={save}
            disabled={writes.saving}
            data-testid="save-agenda-item"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The reschedule form, holding its own times; see `AgendaItemDialog`. */
function RescheduleDialog({
  item,
  timeZone,
  eventId,
  uid,
  command,
  onClose,
}: {
  item: AgendaItem;
  timeZone: string | undefined;
  eventId: string;
  uid: string;
  command: AdminCommand;
  onClose: () => void;
}) {
  const [times, setTimes] = useState({
    startsAt: instantToWallTime(item.startsAt, timeZone),
    endsAt: instantToWallTime(item.endsAt, timeZone),
  });
  const [problem, setProblem] = useState<string | null>(null);
  const busy =
    command.state.status === "submitting" ||
    command.state.status === "watching";

  async function reschedule() {
    const parsed = parseTimes(times, timeZone, "Both times are required.");
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    const { start, end } = parsed;
    setProblem(null);
    await command.submit("RESCHEDULE_AGENDA", eventId, uid, {
      items: [
        {
          itemId: item.id,
          startsAt: Timestamp.fromDate(start),
          endsAt: Timestamp.fromDate(end),
        },
      ],
    });
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reschedule {item.title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <TimeField
            label="Starts"
            testId="reschedule-starts"
            value={times.startsAt}
            onChange={(v) => setTimes({ ...times, startsAt: v })}
          />
          <TimeField
            label="Ends"
            testId="reschedule-ends"
            value={times.endsAt}
            onChange={(v) => setTimes({ ...times, endsAt: v })}
          />
        </div>
        <ClockNote
          timeZone={timeZone}
          times={times}
          testId="reschedule-times-clock"
        />
        {problem && (
          <p
            className="text-sm text-destructive-text"
            data-testid="reschedule-error"
          >
            {problem}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="cta"
            onClick={reschedule}
            disabled={busy}
            data-testid="confirm-reschedule"
          >
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Copied from firestore.rules: isOptionalBoundedString on each. */
const MAX_DESCRIPTION = 100000;
const MAX_ROOM_NAME = 500;
const MAX_LOCATION = 1000;
const MAX_LOCATION_NOTES = 5000;

/**
 * Mirror the bounds the rules enforce, so a refusal is a sentence rather than
 * the permission-denied that names no field.
 */
function firstOptionalProblem(form: {
  description: string;
  roomName: string;
  location: string;
  locationNotes: string;
}): string | null {
  if (form.description.trim().length > MAX_DESCRIPTION)
    return `Description must be ${MAX_DESCRIPTION} characters or fewer.`;
  if (form.roomName.trim().length > MAX_ROOM_NAME)
    return `Room must be ${MAX_ROOM_NAME} characters or fewer.`;
  if (form.location.trim().length > MAX_LOCATION)
    return `Location must be ${MAX_LOCATION} characters or fewer.`;
  if (form.locationNotes.trim().length > MAX_LOCATION_NOTES)
    return `Location notes must be ${MAX_LOCATION_NOTES} characters or fewer.`;
  return null;
}

/** "" on an update means remove the field rather than storing an empty string. */
function optionalText(value: string): string | ReturnType<typeof deleteField> {
  const text = value.trim();
  return text || deleteField();
}

/**
 * Both form times as instants on the event's clock, or what is wrong.
 *
 * A time the clocks skip for daylight saving names no instant at all, and
 * saying so beats moving the session by an hour without a word. A time they
 * pass twice is accepted as its first pass; `ClockNote` says so beside the
 * fields before the organiser saves.
 */
function parseTimes(
  times: { startsAt: string; endsAt: string },
  timeZone: string | undefined,
  missing: string,
): { start: Date; end: Date } | { problem: string } {
  if (!COMPLETE.test(times.startsAt) || !COMPLETE.test(times.endsAt))
    return { problem: missing };
  const start = resolveWallTime(times.startsAt, timeZone);
  const end = resolveWallTime(times.endsAt, timeZone);
  for (const [wall, resolved] of [
    [times.startsAt, start],
    [times.endsAt, end],
  ] as const) {
    if (resolved.kind === "skipped")
      return {
        problem: `${describeWall(wall)} does not exist in ${clockName(timeZone)}, because the clocks go forward that night. Choose a time after the change.`,
      };
  }
  if (!("instant" in start) || !("instant" in end)) return { problem: missing };
  if (end.instant <= start.instant)
    return { problem: "The end time must be after the start time." };
  return { start: start.instant, end: end.instant };
}

/**
 * Which clock the fields above are on, said beside them, and a warning for
 * a time the clocks pass twice, which would otherwise save silently as the
 * first pass.
 */
function ClockNote({
  timeZone,
  times,
  testId,
}: {
  timeZone: string | undefined;
  times: { startsAt: string; endsAt: string };
  testId: string;
}) {
  const repeated = [times.startsAt, times.endsAt].find(
    (wall) =>
      COMPLETE.test(wall) &&
      resolveWallTime(wall, timeZone).kind === "repeated",
  );
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p data-testid={testId}>
        {timeZone
          ? `Enter times in ${clockName(timeZone)} (${timeZone}), the event's time zone.`
          : "Enter times on your device's clock; the event has no time zone this browser knows."}
      </p>
      {repeated && (
        <p role="status" data-testid={`${testId}-repeated`}>
          {describeWall(repeated)} happens twice in {clockName(timeZone)},
          because the clocks go back that night. The first one, before the
          change, is used.
        </p>
      )}
    </div>
  );
}

/**
 * Toggle chips, not a multi-select widget.
 *
 * `trackIds` and `presenterIds` are the two array fields firestore.rules
 * accepts on an agenda item, capped at 50 and 20. A native multi-select is
 * unusable with a keyboard and invisible on touch, and the counts here are
 * small enough that showing every option is the clearer answer.
 *
 * The cap is enforced here as well as in the rules, because exceeding it is a
 * permission-denied with no field name in it.
 */
function MultiPicker({
  label,
  testId,
  options,
  selected,
  onToggle,
  max,
  empty,
}: {
  label: string;
  testId: string;
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (next: string[]) => void;
  max: number;
  empty: string;
}) {
  const atCap = selected.length >= max;

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label>
        {label}
        {selected.length > 0 ? ` (${selected.length})` : ""}
      </Label>
      {options.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {options.map((option) => {
            const on = selected.includes(option.id);
            return (
              <ToggleChip
                key={option.id}
                selected={on}
                // At the cap the unselected chips go dead, but the selected
                // ones must stay clickable or there is no way back under it.
                disabled={!on && atCap}
                data-testid={`${testId}-${option.id}`}
                onClick={() =>
                  onToggle(
                    on
                      ? selected.filter((id) => id !== option.id)
                      : [...selected, option.id],
                  )
                }
              >
                {option.label}
              </ToggleChip>
            );
          })}
        </div>
      )}
      {atCap && (
        <p className="text-xs text-muted-foreground">{max} is the maximum.</p>
      )}
    </div>
  );
}
